import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiRefreshCw, FiSettings } from "react-icons/fi";
import { useNavigate } from "react-router";

import { AVAILIO_LOGO } from "@constants/assets";
import { CONSTANTS } from "@constants/index";
import { kioskRouteEnum } from "@enums/route.enum";
import API_SERVICES from "@services/index";
import commonUtils from "@utils/common.utils";

const KioskDeviceError: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const response = await API_SERVICES.KIOSK.LANDING_PAGE();

      if (response.status === CONSTANTS.HTTP_STATUS.SUCCESS) {
        const { data } = response;

        if (data.clublogoEnabled || data.sponsorEnabled) {
          navigate(kioskRouteEnum.Landing);
        } else {
          navigate(kioskRouteEnum.Booking);
        }

        return;
      }

      commonUtils.showErrorToast(response);
    } catch {
      commonUtils.toastService(
        t("kiosk.deviceError.retryFailed"),
        CONSTANTS.TOAST_TYPE.ERROR
      );
    } finally {
      setIsRetrying(false);
    }
  };

  const handleReconfigure = () => {
    commonUtils.clearUserSession(true);
    navigate(kioskRouteEnum.Login);
  };

  return (
    <div className="kiosk-device-error-wrapper d-flex justify-content-center align-items-center vh-100">
      <div className="kiosk-device-error-card text-center p-5">
        {/* Logo */}
        <div className="d-flex justify-content-center mb-4">
          <img
            src={AVAILIO_LOGO}
            className="kiosk-device-error-logo"
            alt={t("kiosk.deviceError.logoAlt")}
          />
        </div>

        {/* Icon */}
        <div className="d-flex justify-content-center mb-4">
          <FiAlertTriangle className="kiosk-device-error-icon" />
        </div>

        {/* Title */}
        <h1 className="kiosk-device-error-title fw-bold mb-3">
          {t("kiosk.deviceError.title")}
        </h1>

        {/* Message */}
        <p className="kiosk-device-error-message text-muted mb-2">
          {t("kiosk.deviceError.message")}
        </p>
        <p className="kiosk-device-error-hint text-muted mb-3">
          {t("kiosk.deviceError.hint")}
        </p>

        <p className="kiosk-device-error-contact text-muted mb-5">
          {t("kiosk.deviceError.contactAdmin")}
        </p>

        {/* Buttons */}
        <div className="d-flex flex-wrap justify-content-center gap-3">
          <button
            id="kiosk-device-error-retry-btn"
            className="btn btn-outline-secondary btn-lg d-flex align-items-center justify-content-center gap-2"
            onClick={handleRetry}
            disabled={isRetrying}
          >
            <FiRefreshCw className={isRetrying ? "spin" : ""} />
            {isRetrying
              ? t("kiosk.deviceError.retrying")
              : t("kiosk.deviceError.retryButton")}
          </button>

          <button
            id="kiosk-device-error-reconfigure-btn"
            className="btn btn-primary btn-lg d-flex align-items-center justify-content-center gap-2"
            onClick={handleReconfigure}
            disabled={isRetrying}
          >
            <FiSettings />
            {t("kiosk.deviceError.reconfigureButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KioskDeviceError;
